import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { createCampaign, sendCampaign } from '../../services/communications';
import { sessionAPI } from '../../services/api';

/**
 * Admin/superadmin: one-step emergency send (no confirmation — speed first).
 */
export default function EmergencySendDialog({ open, onClose, onSent }) {
  const [bodyPlain, setBodyPlain] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const sessRes = await sessionAPI.getSessions();
        if (cancelled) return;
        const sl = sessRes.data?.results || sessRes.data || [];
        setSessions(Array.isArray(sl) ? sl : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const payload = {
        title: 'Emergency alert',
        body_plain: bodyPlain.trim(),
        category: 'emergency',
        priority: 'emergency',
        audience_type: sessionId ? 'by_session' : 'all_parents',
        session: sessionId ? Number(sessionId) : null,
        classroom_class_num: null,
        specific_student_ids: [],
      };
      const created = await createCampaign(payload);
      const sendRes = await sendCampaign(created.id);
      onSent?.({ ...created, ...(sendRes || {}) });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Emergency broadcast</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends immediately via SMS and voice (TTS). No second confirmation.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="em-sess">Audience</InputLabel>
          <Select
            labelId="em-sess"
            label="Audience"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
          >
            <MenuItem value="">All parents (entire site)</MenuItem>
            {sessions.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Message"
          fullWidth
          multiline
          minRows={5}
          margin="normal"
          value={bodyPlain}
          onChange={(e) => setBodyPlain(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" color="error" size="large" onClick={handleSend} disabled={sending || !bodyPlain.trim()}>
          {sending ? 'Sending…' : 'Send now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
